import ReactDOM from 'react-dom';
import { DEFAULT_MONTHLY_SALARY_COP_STR } from '../utils/employeeRosterForm.js';
import './EmployeeRosterModal.css';

/**
 * Modal unificado de alta/edición de colaborador (misma estructura que Gestión de usuarios).
 */
export default function EmployeeRosterModal({
    form,
    setForm,
    areas = [],
    supervisors = [],
    onSubmit,
    onClose,
    onRetire,
    lockRole = false,
    areasHint,
    saving = false,
}) {
    if (!form) return null;

    const isCreate = form.mode === 'create';
    const title = isCreate ? 'Nuevo colaborador' : 'Editar colaborador';

    const panel = (
        <div className="roster-modal-backdrop" role="presentation" onClick={onClose}>
            <div
                className="glass-card roster-modal-panel erm-panel animate-in"
                role="dialog"
                aria-modal="true"
                aria-labelledby="erm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="erm-title">{title}</h2>
                <form
                    className="erm-form-grid"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit(e);
                    }}
                >
                    <div className="erm-field erm-field--full">
                        <label>Documento de identificación</label>
                        <input
                            className="input w-100"
                            required
                            value={form.id_document}
                            onChange={(e) => setForm((m) => ({ ...m, id_document: e.target.value }))}
                        />
                    </div>
                    <div className="erm-field">
                        <label>Código de empleado (acceso)</label>
                        <input
                            className="input w-100"
                            value={form.employee_code}
                            onChange={(e) => setForm((m) => ({ ...m, employee_code: e.target.value }))}
                            disabled={!isCreate}
                            placeholder="Si vacío en alta, se usa la identificación"
                        />
                    </div>
                    <div className="erm-field">
                        <label>Fecha de nacimiento</label>
                        <input
                            className="input w-100"
                            type="date"
                            required
                            value={form.birth_date}
                            onChange={(e) => setForm((m) => ({ ...m, birth_date: e.target.value }))}
                        />
                    </div>
                    <div className="erm-field erm-field--full">
                        <label>Nombre completo</label>
                        <input
                            className="input w-100"
                            required
                            value={form.full_name}
                            onChange={(e) => setForm((m) => ({ ...m, full_name: e.target.value }))}
                        />
                    </div>
                    <div className="erm-field">
                        <label>Rol</label>
                        <select
                            className="input w-100"
                            value={form.role}
                            disabled={lockRole}
                            onChange={(e) => {
                                const r = e.target.value;
                                setForm((m) => ({
                                    ...m,
                                    role: r,
                                    monthly_salary:
                                        r === 'worker' &&
                                        (!m.monthly_salary ||
                                            String(m.monthly_salary).trim() === '' ||
                                            Number(m.monthly_salary) <= 0)
                                            ? DEFAULT_MONTHLY_SALARY_COP_STR
                                            : m.monthly_salary,
                                }));
                            }}
                        >
                            <option value="worker">Colaborador</option>
                            <option value="supervisor">Supervisor</option>
                            <option value="admin">Admin empresa</option>
                        </select>
                    </div>
                    <div className="erm-field">
                        <label>Área</label>
                        <select
                            className="input w-100"
                            required
                            value={form.area_id}
                            onChange={(e) => setForm((m) => ({ ...m, area_id: e.target.value }))}
                        >
                            <option value="">— Selecciona —</option>
                            {areas.map((a) => (
                                <option key={a.id} value={a.id}>
                                    {a.name}
                                </option>
                            ))}
                        </select>
                        {areasHint ? <p className="erm-hint">{areasHint}</p> : null}
                        {!areas.length && !areasHint ? (
                            <p className="erm-hint">No hay áreas cargadas. Recargue la página o cree áreas en Base de personal.</p>
                        ) : null}
                    </div>
                    <div className="erm-field">
                        <label>Supervisor (opcional)</label>
                        <select
                            className="input w-100"
                            value={form.supervisor_id}
                            onChange={(e) => setForm((m) => ({ ...m, supervisor_id: e.target.value }))}
                        >
                            <option value="">— Ninguno —</option>
                            {supervisors
                                .filter((s) => s.id !== form.id)
                                .map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.full_name} ({s.employee_code})
                                    </option>
                                ))}
                        </select>
                    </div>
                    <div className="erm-field">
                        <label>Cargo</label>
                        <input
                            className="input w-100"
                            value={form.position}
                            onChange={(e) => setForm((m) => ({ ...m, position: e.target.value }))}
                            placeholder="Ej. Operador de línea"
                        />
                    </div>
                    <div className="erm-field erm-field--full">
                        <label>Sueldo mensual (COP)</label>
                        <input
                            className="input w-100"
                            type="number"
                            min="0"
                            step="1"
                            value={form.monthly_salary}
                            onChange={(e) => setForm((m) => ({ ...m, monthly_salary: e.target.value }))}
                            placeholder="2000000"
                        />
                        <p className="erm-hint">Valores en pesos colombianos (sin decimales). Usado en la calculadora CID.</p>
                    </div>
                    <div className="erm-field">
                        <label>Fecha de ingreso (opcional)</label>
                        <input
                            className="input w-100"
                            type="date"
                            value={form.hire_date}
                            onChange={(e) => setForm((m) => ({ ...m, hire_date: e.target.value }))}
                        />
                    </div>
                    {isCreate && form.role !== 'worker' ? (
                        <div className="erm-field erm-field--full">
                            <label>Contraseña inicial</label>
                            <input
                                className="input w-100"
                                type="password"
                                autoComplete="new-password"
                                value={form.password}
                                onChange={(e) => setForm((m) => ({ ...m, password: e.target.value }))}
                                placeholder="Mínimo 6 caracteres"
                                minLength={6}
                                required
                            />
                        </div>
                    ) : null}

                    <div className="erm-actions erm-field--full">
                        {!isCreate && onRetire ? (
                            <button type="button" className="btn btn-secondary erm-retire-btn" onClick={onRetire}>
                                Retirar colaborador
                            </button>
                        ) : (
                            <span />
                        )}
                        <div className="erm-actions-right">
                            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                                Cancelar
                            </button>
                            <button type="submit" className="btn btn-primary" disabled={saving}>
                                {saving ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );

    return ReactDOM.createPortal(panel, document.body);
}
